import {
  AcidIcon,
  BludgeoningIcon,
  ColdIcon,
  FireIcon,
  ForceIcon,
  HealingIcon,
  LightningIcon,
  NecroticIcon,
  PiercingIcon,
  PoisonIcon,
  PsychicIcon,
  RadiantIcon,
  SlashingIcon,
  ThunderIcon,
} from "components/ui/icons/SpellIcons";
import React from "react";

const actionTypes = [
  { key: "util", label: "Utility" },
  { key: "save", label: "Save" },
  { key: "msak", label: "Melee" },
  { key: "rsak", label: "Ranged" },
  { key: "heal", label: "Heal" },
  { key: "other", label: "Other" },
];

const attributes = [
  { key: "str", label: "Strength" },
  { key: "dex", label: "Dexterity" },
  { key: "con", label: "Constitution" },
  { key: "int", label: "Intelligence" },
  { key: "wis", label: "Wisdom" },
  { key: "cha", label: "Charisma" },
];

export const spellSchools = [
  { key: "con", label: "Conjuration" },
  { key: "nec", label: "Necromancy" },
  { key: "evo", label: "Evocation" },
  { key: "abj", label: "Abjuration" },
  { key: "trs", label: "Transmutation" },
  { key: "div", label: "Divination" },
  { key: "enc", label: "Enchantment" },
  { key: "ill", label: "Illusion" },
  { key: "psi", label: "Psionics" },
];

export const spellLevel = [
  { key: "0", label: "Cantrip", color: "#3b021c" },
  { key: "1", label: "1st level", color: "#fa7a63" },
  { key: "2", label: "2nd level", color: "#f05c60" },
  { key: "3", label: "3rd level", color: "#d9485e" },
  { key: "4", label: "4th level", color: "#bd395d" },
  { key: "5", label: "5th level", color: "#9e2f5c" },
  { key: "6", label: "6th level", color: "#7f275b" },
  { key: "7", label: "7th level", color: "#5e1f5a" },
  { key: "8", label: "8th level", color: "#3b1859" },
  { key: "9", label: "9th level", color: "#001258" },
];

export const damageTypes = [
  { key: "acid", color: "#13ba7d", icon: <AcidIcon /> },
  { key: "bludgeoning", color: "#5d3218", icon: <BludgeoningIcon /> },
  { key: "cold", color: "#1788c8", icon: <ColdIcon /> },
  { key: "fire", color: "#ca3421", icon: <FireIcon /> },
  { key: "force", color: "#3831d4", icon: <ForceIcon /> },
  { key: "lightning", color: "#08b8b8", icon: <LightningIcon /> },
  { key: "necrotic", color: "#165339", icon: <NecroticIcon /> },
  { key: "piercing", color: "#5f524e", icon: <PiercingIcon /> },
  { key: "poison", color: "#76c220", icon: <PoisonIcon /> },
  { key: "psychic", color: "#d41c5c", icon: <PsychicIcon /> },
  { key: "radiant", color: "#ec6f01", icon: <RadiantIcon /> },
  { key: "slashing", color: "#5a3535", icon: <SlashingIcon /> },
  { key: "thunder", color: "#4c00ff", icon: <ThunderIcon /> },
  { key: "healing", color: "#ca0a28", icon: <HealingIcon /> },
  { key: "temphp", color: "#014b9d", icon: <HealingIcon /> },
];

export const getSpellLevelProperty = (
  level: number,
  prop: "label" | "color"
) => {
  return spellLevel.find((k) => k.key === `${level}`)?.[prop];
};

export const getSpellDamageType = (
  type: string,
  prop: "key" | "color" | "all"
) => {
  const optn = damageTypes.find((k) => k.key === type);
  return prop === "all" ? optn : optn?.[prop];
};

export const getSpellSchool = (school: string) => {
  return spellSchools.find((s) => s.key === school)?.label;
};

export const getSpellActionType = (type: string) => {
  return actionTypes.find((s) => s.key === type)?.label;
};

export const formatSpellActionType = (
  type: string,
  save?: { ability: string }
) => {
  const aType = getSpellActionType(type);
  return aType === "Save" ? `${save?.ability.toUpperCase()} Save` : aType;
};

const spellSchoolsValues = spellSchools.map((s) => s.key);

export interface ISpellDuration {
  value: number | null;
  units: string;
}
export interface ISpellActivation {
  type: string;
  cost: number;
  condition: string;
}
export interface ISpellComponents {
  value: string;
  vocal: boolean;
  somatic: boolean;
  material: boolean;
  ritual: boolean;
  concentration: boolean;
}
export interface ISpellMaterials {
  value: string;
  consumed: boolean;
  cost: number;
  supply: number;
}
export interface ISpellTarget {
  value: null | string;
  units: string;
  type: string;
}
export interface ISpellRange {
  value: null | number;
  long: null | string;
  units: null | string;
}

export interface ISpellData {
  description: {
    value: string;
  };
  level: number;
  school: typeof spellSchoolsValues[number];
  actionType: string;
  save?: {
    ability: string;
  };
  activation: ISpellActivation;
  duration: ISpellDuration;
  target: ISpellTarget;
  range: ISpellRange;
  components: ISpellComponents;
  materials: ISpellMaterials;
  damage: {
    parts: [string, string][];
  };
}

export interface ISpell {
  name: string;
  data: ISpellData;
  img?: string;
  fields?: {
    slug: string;
    classes: string[];
    subclasses: string[];
    damage: string[];
    homebrew: boolean;
  };
}

export const defaultSpellFields = {
  classes: [],
  subclasses: [],
  damage: [],
  homebrew: false,
};
